import { logAmlCheck } from './pbService';

const KEY = process.env.VUE_APP_AMLBOT_KEY || '';
const API = 'https://amlbot.com/api/v1/';
const CACHE = new Map();
const TTL = 10 * 60 * 1000;

// Hard-blocked signal names — no override allowed
const HARD_BLOCK = new Set(['sanctions']);
// Soft-blocked — blocked by default but user can override
const SOFT_BLOCK = new Set(['darknet_service', 'mixer', 'theft', 'ransomware', 'scam', 'phishing']);

export const CHAIN_AML = {
  tron:     'TRX',
  ethereum: 'ETH',
  bsc:      'BSC',
  polygon:  'MATIC',
  1:        'ETH',
  56:       'BSC',
  137:      'MATIC',
};

export const RISK_COLOR = {
  low:      '#28a745',
  medium:   '#ffc107',
  high:     '#fd7e14',
  critical: '#dc3545',
  unknown:  '#6c757d',
};

export const RISK_LABEL = {
  low:      'LOW',
  medium:   'MEDIUM',
  high:     'HIGH',
  critical: 'CRITICAL',
  unknown:  '—',
};

export const SIGNAL_META = {
  sanctions:        { label: 'SANCTIONED',  icon: '🚫', hardBlock: true  },
  darknet_service:  { label: 'DARKNET',     icon: '☠',  hardBlock: false },
  mixer:            { label: 'MIXER',       icon: '🌀', hardBlock: false },
  theft:            { label: 'THEFT',       icon: '⚠',  hardBlock: false },
  ransomware:       { label: 'RANSOMWARE',  icon: '⚠',  hardBlock: false },
  scam:             { label: 'SCAM',        icon: '⚠',  hardBlock: false },
  phishing:         { label: 'PHISHING',    icon: '⚠',  hardBlock: false },
  exchange:         { label: 'EXCHANGE',    icon: '🏦', hardBlock: false },
  gambling:         { label: 'GAMBLING',    icon: '🎲', hardBlock: false },
  p2p_exchange:     { label: 'P2P',         icon: '🔄', hardBlock: false },
};

/**
 * Check an address against AMLBot.
 * Returns a normalised result object regardless of API availability.
 *
 * @param {string} address
 * @param {string} blockchain  - 'TRX' | 'ETH' | 'BSC' | 'MATIC'
 * @returns {Promise<{
 *   risk_score: number,
 *   risk_level: string,
 *   signals: Array<{name:string, score:number}>,
 *   hardBlocked: boolean,
 *   softBlocked: boolean,
 *   error: string|null
 * }>}
 */
export async function checkAddress(address, blockchain) {
  if (!address) return _empty();
  const cacheKey = `${blockchain}:${address.toLowerCase()}`;
  const cached = CACHE.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < TTL) return cached.result;

  if (!KEY) {
    const result = { ..._empty(), error: 'No AMLBot API key (VUE_APP_AMLBOT_KEY)' };
    CACHE.set(cacheKey, { result, fetchedAt: Date.now() });
    return result;
  }

  try {
    const res = await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'api-key': KEY },
      body: JSON.stringify({ address, blockchain }),
      signal: AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
    });

    if (!res.ok) throw new Error(`AMLBot HTTP ${res.status}`);
    const data = await res.json();

    const signals = Array.isArray(data.signals) ? data.signals : [];
    const signalNames = new Set(signals.map(s => s.name));
    const hardBlocked = [...HARD_BLOCK].some(s => signalNames.has(s));
    const softBlocked = !hardBlocked && [...SOFT_BLOCK].some(s => signalNames.has(s));

    const result = {
      risk_score:  data.risk_score  ?? 0,
      risk_level:  data.risk_level  ?? 'unknown',
      signals,
      hardBlocked,
      softBlocked,
      error: null,
    };
    CACHE.set(cacheKey, { result, fetchedAt: Date.now() });
    // Persist to DB (non-blocking)
    logAmlCheck({
      address,
      chain: blockchain,
      risk_score:   result.risk_score,
      risk_level:   result.risk_level,
      signals:      result.signals,
      hard_blocked: result.hardBlocked,
      soft_blocked: result.softBlocked,
    });
    return result;
  } catch (e) {
    const result = { ..._empty(), error: e.message };
    CACHE.set(cacheKey, { result, fetchedAt: Date.now() - TTL + 15000 }); // retry in 15s on error
    return result;
  }
}

/** Check multiple addresses in parallel, returns Map<"blockchain:address" → result> */
export async function checkAddresses(entries) {
  const results = await Promise.allSettled(
    entries.map(({ address, blockchain }) => checkAddress(address, blockchain))
  );
  const map = new Map();
  entries.forEach(({ address, blockchain }, i) => {
    const key = `${blockchain}:${address.toLowerCase()}`;
    map.set(key, results[i].status === 'fulfilled' ? results[i].value : _empty());
  });
  return map;
}

export function clearCache() { CACHE.clear(); }

function _empty() {
  return { risk_score: 0, risk_level: 'unknown', signals: [], hardBlocked: false, softBlocked: false, error: null };
}
